import React, {useState} from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
} from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrTooltip from '../../../../common/arr-tooltip/ArrTooltip';
import './QuestionReadOnlyList.scss';
import {useTranslation} from 'react-i18next';

export interface QuestionReadOnlyListItem {
  key: string;
  id?: string;
  description: string;
  title?: string;
  tag?: string;
}

export interface QuestionReadOnlyListSection {
  id: string;
  label: string;
  tooltipContent?: string;
  defaultExpanded?: boolean;
  items: QuestionReadOnlyListItem[];
}

interface QuestionReadOnlyListProps {
  sections: QuestionReadOnlyListSection[];
  dataTestId?: string;
  className?: string;
}

const QuestionReadOnlyList: React.FC<QuestionReadOnlyListProps> = ({
  sections,
  dataTestId = 'question-read-only-list',
  className = '',
}) => {
  const [expandedMap, setExpandedMap] = useState<Record<string, boolean>>(() =>
    sections.reduce<Record<string, boolean>>((acc, section) => {
      acc[section.id] = section.defaultExpanded ?? true;
      return acc;
    }, {})
  );
  const {t} = useTranslation();
  const renderListBody = (section: QuestionReadOnlyListSection) => (
    <Box
      className={`question-read-only-list ${className}`.trim()}
      data-testid={`${dataTestId}-${section.id}`}
    >
      {(section.items?.length ?? 0) === 0 ? (
        <Box
          className="question-read-only-list__empty"
          data-testid={`${dataTestId}-${section.id}-empty`}
        >
          {t('questionReadOnlyList.none')}
        </Box>
      ) : (
        section.items.map((item) => (
          <Box
            key={item.key}
            className="question-read-only-list-item question-read-only-list-item--tag-description"
            data-testid={`${dataTestId}-${section.id}-${item.key}`}
          >
            <Box className="question-read-only-list-item__tag-content">
              <Box className="question-read-only-list-item__tag-row">
                {item.id && (
                  <Box
                    className="question-read-only-list-item__tag"
                    data-testid={`${dataTestId}-${section.id}-${item.key}-id`}
                  >
                    {item.id}
                  </Box>
                )}
                <Box className="question-read-only-list-item__tag-text-content">
                  {item.title && (
                    <Typography
                      className="question-read-only-list-item__tag-title typo-text subhead4_size text-headings"
                      data-testid={`${dataTestId}-${section.id}-${item.key}-title`}
                    >
                      {item.title}
                    </Typography>
                  )}
                  <Typography
                    className="question-read-only-list-item__tag-description"
                    data-testid={`${dataTestId}-${section.id}-${item.key}-description`}
                  >
                    {item.description}
                  </Typography>
                  {item.tag && (
                    <Typography
                      className="question-read-only-list-item__tag-note"
                      data-testid={`${dataTestId}-${section.id}-${item.key}-tag`}
                    >
                      {t('questionReadOnlyList.tag')}{' '}
                      <span className="question-read-only-list-item__tag-note-value">
                        {item.tag}
                      </span>
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        ))
      )}
    </Box>
  );

  return (
    <Box
      className="question-read-only-list-sections"
      data-testid={`${dataTestId}-sections`}
    >
      {sections.map((section, index) => {
        const expanded = expandedMap[section.id] ?? true;
        const isFirstSection = index === 0;
        const isLastSection = index === sections.length - 1;
        return (
          <Accordion
            key={section.id}
            disableGutters
            elevation={0}
            expanded={expanded}
            onChange={() =>
              setExpandedMap((prev) => ({
                ...prev,
                [section.id]: !expanded,
              }))
            }
            className={`question-read-only-list-accordion${
              isFirstSection ? ' question-read-only-list-accordion--first' : ''
            }${isLastSection ? ' question-read-only-list-accordion--last' : ''}`}
            data-testid={`${dataTestId}-${section.id}-accordion`}
          >
            <AccordionSummary
              className="question-read-only-list-accordion__header"
              expandIcon={
                <span className="question-read-only-list-accordion__expand-icon">
                  {expanded ? (
                    <KeyboardArrowUpIcon />
                  ) : (
                    <KeyboardArrowDownIcon />
                  )}
                </span>
              }
            >
              <Box className="question-read-only-list-accordion__title-wrapper">
                <Typography className="question-read-only-list-accordion__title typo-text subhead4_size text-headings">
                  {section.label}
                </Typography>

                {section.tooltipContent && (
                  <span
                    className="question-read-only-list-accordion__info-tooltip"
                    onClick={(event) => event.stopPropagation()}
                    onMouseDown={(event) => event.stopPropagation()}
                  >
                    <ArrTooltip
                      content={section.tooltipContent}
                      placement="top"
                      tooltipKey={section.id}
                    >
                      <InfoOutlinedIcon
                        className="question-read-only-list-accordion__info-icon"
                        role="img"
                        aria-label="More information"
                      />
                    </ArrTooltip>
                  </span>
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails className="question-read-only-list-accordion__body">
              {renderListBody(section)}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};

export default QuestionReadOnlyList;
